import { computed, onScopeDispose, ref, shallowRef } from 'vue'
import type { Mode, Status, VoiceConversation } from '@elevenlabs/client'
import { logger } from 'utils'

/** What the backend hands over to open a session, and nothing more. */
export type VoiceSessionGrant = {
    /** Opaque server-side session id, echoed back to us through the provider. */
    sessionId: string
    signedUrl: string
    /** Conversation the spoken turns are persisted into. */
    chatId: string
    maxSessionSeconds: number
}

export type VoiceTranscriptEntry = {
    role: 'user' | 'agent'
    text: string
}

/** Which side of the conversation an audio level belongs to. */
export type VoiceChannel = 'input' | 'output'

/**
 * A voice conversation with one of our agents.
 *
 * The provider handles microphone, turn detection and playback; the turn itself
 * is answered by our own pipeline, because the provider-side agent is configured
 * to call our OpenAI-compatible surface as a custom LLM. So there is no chat
 * logic here: this composable owns the session lifecycle, the visible state, the
 * duration cap and the audio levels the visualiser reads, and nothing else.
 *
 * `fetchGrant` and `closeGrant` are injected rather than called from here, so the
 * package stays free of any knowledge about backend URLs or authentication.
 */
export const useVoiceSession = (options: {
    fetchGrant: () => Promise<VoiceSessionGrant>
    /** Ends the server-side session, which is also what bills its minutes. */
    closeGrant?: (sessionId: string) => Promise<void>
    onTranscript?: (entry: VoiceTranscriptEntry) => void
    /**
     * The agent stopped speaking, so the conversation persisted server-side is
     * now ahead of what the client shows.
     */
    onTurnEnd?: () => void
}) => {
    const conversation = shallowRef<VoiceConversation>()
    const status = ref<Status>('disconnected')
    const mode = ref<Mode>('listening')
    const error = ref<string>()
    const transcript = ref<VoiceTranscriptEntry[]>([])
    const remainingSeconds = ref<number>()
    const chatId = ref<string>()

    let sessionId: string | undefined
    let capTimer: ReturnType<typeof setInterval> | undefined
    /**
     * Bumped by every start and every stop. The provider's callbacks arrive
     * asynchronously and a late one from a closed session would otherwise clear
     * the state of the next: it would wipe the transcript on screen and close
     * the server-side session that is currently billing. Every callback checks
     * the generation it was registered for.
     */
    let generation = 0
    let disposed = false

    const clearCap = () => {
        if (capTimer) {
            clearInterval(capTimer)
            capTimer = undefined
        }
        remainingSeconds.value = undefined
    }

    /**
     * The provider enforces the same cap on its side (and our backend refuses a
     * turn past it), but a client that stops on its own is what keeps an idle tab
     * from burning provider minutes in the meantime.
     */
    const startCap = (maxSessionSeconds: number) => {
        // Counted from a deadline instead of decremented once per tick: a
        // background tab throttles intervals to about one a minute, and a
        // countdown would have shown minutes left on a call the provider had
        // already hung up, while our own stop never fired.
        const endsAt = Date.now() + maxSessionSeconds * 1000
        const tick = () => {
            const left = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000))
            remainingSeconds.value = left
            if (left === 0) {
                void stop()
            }
        }
        tick()
        capTimer = setInterval(tick, 1000)
    }

    /**
     * Closes one server-side session, exactly once, and only the one it was
     * given. Reading the shared `sessionId` here was a real hazard: a `stop()`
     * still awaiting `endSession()` would pick up the id a new `start()` had
     * just stored and close the session that was live, leaving the provider
     * talking to a record we had deleted (every turn refused, minutes billed).
     */
    const releaseGrant = async (id: string | undefined) => {
        if (!id) {
            return
        }
        if (sessionId === id) {
            sessionId = undefined
        }
        await options.closeGrant?.(id).catch(() => undefined)
    }

    const stop = async () => {
        generation += 1
        clearCap()
        const mine = sessionId
        sessionId = undefined
        const active = conversation.value
        conversation.value = undefined
        await active?.endSession().catch(() => undefined)
        await releaseGrant(mine)
        status.value = 'disconnected'
        mode.value = 'listening'
    }

    const start = async () => {
        if (disposed || conversation.value || status.value === 'connecting') {
            return
        }
        error.value = undefined
        transcript.value = []
        // Both would otherwise survive into the next call: a stale 'speaking'
        // makes the first mode change of the new one look like the end of a turn
        // and fires a reload for a turn that never happened.
        mode.value = 'listening'
        chatId.value = undefined
        status.value = 'connecting'
        generation += 1
        const mine = generation
        const isCurrent = () => mine === generation && !disposed
        /**
         * The grant this attempt opened, if it got that far. The failure path
         * reads it instead of the shared `sessionId`, for the same reason
         * `releaseGrant` takes an id rather than reading one.
         */
        let ownSessionId: string | undefined

        try {
            // The provider SDK is loaded here rather than imported at the top:
            // it carries a WebRTC stack that weighs more than the rest of the
            // widget put together, and a page that never opens a call should
            // not pay for it. Started before the grant so it downloads while
            // the backend prepares the session, and awaited after it, so a
            // failure lands in the same catch that releases the grant. Its
            // rejection is claimed right away, because nothing awaits the
            // promise until then and the browser would report it as unhandled.
            const providerModule = import('@elevenlabs/client')
            void providerModule.catch(() => undefined)

            const grant = await options.fetchGrant()
            // The surface may have unmounted, or the user may have given up,
            // while the grant was in flight: closing it here is what keeps an
            // orphan session from billing minutes nobody is listening to.
            if (!isCurrent()) {
                await options
                    .closeGrant?.(grant.sessionId)
                    .catch(() => undefined)
                return
            }
            sessionId = grant.sessionId
            ownSessionId = grant.sessionId
            chatId.value = grant.chatId
            const { VoiceConversation } = await providerModule
            // Downloading the SDK is a wait the caller can give up on, unlike
            // the ones before it: opening the socket now would start provider
            // minutes for a call nobody is on any more.
            if (!isCurrent()) {
                await releaseGrant(grant.sessionId)
                return
            }
            const conversationStarted = await VoiceConversation.startSession({
                signedUrl: grant.signedUrl,
                // The only channel from this browser to our own callback: the
                // provider forwards it verbatim, and it is what tells the turn
                // which conversation (and which user) it belongs to. Requires
                // the extra-body override to be allowed on the provider agent,
                // which the backend sets when it provisions it.
                customLlmExtraBody: { voice_session_id: grant.sessionId },
                onStatusChange: ({ status: next }) => {
                    if (!isCurrent()) {
                        return
                    }
                    status.value = next
                },
                onModeChange: ({ mode: next }) => {
                    if (!isCurrent()) {
                        return
                    }
                    const wasSpeaking = mode.value === 'speaking'
                    mode.value = next
                    if (wasSpeaking && next !== 'speaking') {
                        options.onTurnEnd?.()
                    }
                },
                onMessage: ({ message, role }) => {
                    if (!isCurrent()) {
                        return
                    }
                    const entry = { role, text: message }
                    transcript.value = [...transcript.value, entry]
                    options.onTranscript?.(entry)
                },
                onError: (message) => {
                    if (!isCurrent()) {
                        return
                    }
                    error.value = message
                },
                onDisconnect: (details) => {
                    if (!isCurrent()) {
                        return
                    }
                    clearCap()
                    conversation.value = undefined
                    // Said here rather than trusted to arrive: the interface and
                    // the reload poll both read `status`, and a disconnect that
                    // left it on 'connected' showed a live call that was over.
                    status.value = 'disconnected'
                    mode.value = 'listening'
                    if (details.reason === 'error') {
                        error.value = details.message
                    }
                    void releaseGrant(grant.sessionId)
                    options.onTurnEnd?.()
                },
            })

            // Closed while the socket was opening: end it rather than leave a
            // live conversation nothing holds a reference to.
            if (!isCurrent()) {
                await conversationStarted.endSession().catch(() => undefined)
                await releaseGrant(grant.sessionId)
                return
            }
            conversation.value = conversationStarted
            startCap(grant.maxSessionSeconds)
        } catch (caught) {
            // Only ever this attempt's own grant: reading the shared id here
            // closed the session a newer attempt had just opened, and the
            // clearing below took its live conversation and its countdown with
            // it. That leaves the provider talking to a record we deleted, with
            // nothing left on this side to hang up on: every turn refused,
            // minutes billed. A user who starts a call, gives up and starts
            // another is all it takes.
            await releaseGrant(ownSessionId)
            if (!isCurrent()) {
                return
            }
            conversation.value = undefined
            clearCap()
            status.value = 'disconnected'
            error.value =
                caught instanceof Error ? caught.message : String(caught)
        }
    }

    /** Puts the failure away so the input can come back. */
    const dismissError = () => {
        error.value = undefined
    }

    const setMuted = (muted: boolean) => {
        conversation.value?.setMicMuted(muted)
    }

    /**
     * Says something on the caller's behalf, without the microphone.
     *
     * What the caller answers on screen during a call (a form, a set of
     * options) has no way back to the agent otherwise: the turn that drew the
     * widget is long over, and nothing the browser posts reaches a conversation
     * the provider is driving. Sent this way it becomes an ordinary user turn,
     * answered out loud like any other.
     *
     * Returns false when there is no live call to say it into, so the caller
     * can fall back to the written path.
     */
    const sendUserMessage = (text: string): boolean => {
        const active = conversation.value
        if (!active || !text.trim()) {
            return false
        }
        try {
            active.sendUserMessage(text)
            return true
        } catch (caught) {
            logger.warn('The spoken answer could not be sent', caught)
            return false
        }
    }

    /**
     * Instantaneous loudness of one side, 0 to 1. A function rather than a ref on
     * purpose: the visualiser samples it once per animation frame, and pushing 60
     * reactive writes a second through the whole component tree would cost more
     * than the animation itself.
     */
    const readLevel = (channel: VoiceChannel): number => {
        const active = conversation.value
        if (!active) {
            return 0
        }
        try {
            return channel === 'input'
                ? active.getInputVolume()
                : active.getOutputVolume()
        } catch {
            // The analyser is gone (session closing): silence is the truth.
            return 0
        }
    }

    /** Frequency bins of one side, for a visualiser with actual character. */
    const readSpectrum = (channel: VoiceChannel): Uint8Array | undefined => {
        const active = conversation.value
        if (!active) {
            return undefined
        }
        try {
            return channel === 'input'
                ? active.getInputByteFrequencyData()
                : active.getOutputByteFrequencyData()
        } catch {
            return undefined
        }
    }

    // A session outlives a route change unless it is closed here: the provider
    // keeps billing a socket nobody is listening to. A call in progress when the
    // tab goes away gets the same treatment, and `closeGrant` is what settles
    // its minutes.
    const releaseOnPageHide = () => {
        // Order matters here and nowhere else: the page may be killed at any
        // moment, so the request that settles the minutes goes out first (it is
        // sent with `keepalive`, which cannot help a fetch that never started),
        // and only then do we ask the provider to end the socket.
        const mine = sessionId
        sessionId = undefined
        generation += 1
        clearCap()
        void releaseGrant(mine)
        const active = conversation.value
        conversation.value = undefined
        void active?.endSession().catch(() => undefined)
        status.value = 'disconnected'
    }
    if (typeof window !== 'undefined') {
        window.addEventListener('pagehide', releaseOnPageHide)
    }

    onScopeDispose(() => {
        disposed = true
        if (typeof window !== 'undefined') {
            window.removeEventListener('pagehide', releaseOnPageHide)
        }
        void stop()
    })

    return {
        status,
        mode,
        error,
        transcript,
        remainingSeconds,
        chatId,
        isActive: computed(
            () => status.value === 'connected' || status.value === 'connecting',
        ),
        isConnecting: computed(() => status.value === 'connecting'),
        isSpeaking: computed(
            () => status.value === 'connected' && mode.value === 'speaking',
        ),
        isListening: computed(
            () => status.value === 'connected' && mode.value === 'listening',
        ),
        start,
        stop,
        dismissError,
        setMuted,
        sendUserMessage,
        readLevel,
        readSpectrum,
    }
}
